import { getFormFieldErrorMsg } from "@tm/insurance/utils";
import { theme } from "@tm/utility/global";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import CountryCodeInput from "../CountryCodeInput/CountryCodeInput";
import FlexComp from "../FlexComp/FlexComp";
import FormattedText from "../FormattedText/FormattedText";
import LandingInput from "../LandingInput/LandingInput";
import SpanTag from "../TextStyles/SpanTag";
import {
  PrimaryContactContainer,
  PrimaryContactNote,
} from "./PrimaryContactComp.styles";

interface IPrimaryContact {
  primaryContactData: {
    email: string;
    mobile: string;
  };
}

type FieldProps = {
  countryCode?: string;
  errorMessage: string;
  placeholder: string;
  fieldName: string;
  isRequired: boolean;
  regex: string;
  title: string;
  value: string;
  isEnabled: boolean;
};

type PrimaryContactProps = {
  primaryContact: {
    title: string;
    info: string;
    isEditable?: boolean;
    fields: {
      mobile?: FieldProps;
      email?: FieldProps;
    };
  };
  setValue: any;
  note?: any;
  saveFormValuesOnBlur?: () => void;
  handleCountryCodeModal?: () => void;
  selectedCountry?: any;
  isLoading?: boolean;
};

const PrimaryContactComp = forwardRef<HTMLDivElement, PrimaryContactProps>(
  (
    {
      primaryContact,
      note,
      setValue,
      saveFormValuesOnBlur,
      handleCountryCodeModal,
      selectedCountry,
      isLoading,
    },
    ref
  ) => {
    const { title, info, isEditable, fields } = primaryContact || {};
    const { email, mobile } = fields || {};

    const {
      control,
      formState: { errors },
    } = useFormContext<IPrimaryContact>();

    const internalRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => internalRef.current!);

    useEffect(() => {
      setValue("primaryContactData", {
        mobile: primaryContact?.fields?.mobile?.value || "",
        email: primaryContact?.fields?.email?.value || "",
      });
    }, [primaryContact]);

    return (
      <PrimaryContactContainer ref={internalRef}>
        <FlexComp flexDirection="column" gap="8px" margin="0 0 16px 0">
          <FormattedText
            value={title}
            baseStyle={{
              fontSize: "22px",
              fontWeight: "900",
              color: "#000000",
            }}
          />
          <FormattedText
            value={info}
            baseStyle={{
              fontSize: "14px",
              fontWeight: "400",
              color: "#4a4a4a",
            }}
          />
        </FlexComp>
        <FlexComp flexDirection={"row"} gap="16px">
          {mobile ? (
            <Controller
              control={control}
              name={"primaryContactData.mobile"}
              rules={{
                required: {
                  value: mobile.isRequired ?? true,
                  message: getFormFieldErrorMsg("mobile"),
                },
                pattern: {
                  value: new RegExp(mobile.regex || ".*"),
                  message: getFormFieldErrorMsg("mobile", mobile.errorMessage),
                },
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <CountryCodeInput
                  errorState={errors?.primaryContactData?.mobile?.message}
                  inputValue={value}
                  handleChange={onChange}
                  handleBlur={() => {
                    saveFormValuesOnBlur?.();
                    onBlur();
                  }}
                  placeholderTxt={mobile.placeholder}
                  requiredField={mobile.isRequired}
                  labelTxt={mobile.title}
                  inputType="tel"
                  disableState={isLoading}
                  autoComplete="off"
                  flex="1"
                  inputWidth={email ? "50%" : "100%"}
                  handleCountryCodeModal={handleCountryCodeModal}
                  countryFlag={selectedCountry?.countryFlag}
                  countryCode={selectedCountry?.countryCode || "+91"}
                  isDisabled={!isEditable}
                  backgroundColor={"#F2F2F2"}
                />
              )}
            />
          ) : null}
          {email ? (
            <Controller
              control={control}
              name={"primaryContactData.email"}
              rules={{
                required: {
                  value: email.isRequired ?? true,
                  message: getFormFieldErrorMsg("email"),
                },
                pattern: {
                  value: new RegExp(email.regex || ".*"),
                  message: getFormFieldErrorMsg("email", email.errorMessage),
                },
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <LandingInput
                  inputValue={value}
                  handleChange={onChange}
                  handleBlur={() => {
                    saveFormValuesOnBlur?.();
                    onBlur();
                  }}
                  placeholderTxt={email.placeholder}
                  requiredField={email.isRequired}
                  labelTxt={email.title}
                  errorState={errors?.primaryContactData?.email?.message}
                  disableState={isLoading}
                  autoComplete="off"
                  flex="1"
                  inputWidth={mobile ? "50%" : "100%"}
                  isDisabled={!isEditable}
                  backgroundColor={theme.disabledGrey2}
                />
              )}
            />
          ) : null}
        </FlexComp>
        {note && (
          <PrimaryContactNote>
            <SpanTag color={theme.brownText} fontWeight="bold">
              <FormattedText value={note.title} />
            </SpanTag>
            <SpanTag color={theme.brownText}>
              <FormattedText value={note?.info} />
            </SpanTag>
          </PrimaryContactNote>
        )}
      </PrimaryContactContainer>
    );
  }
);

PrimaryContactComp.displayName = "PrimaryContactComp";

export default PrimaryContactComp;
